import { useCallback, useEffect, useMemo, useState } from 'react';
import { BarChart3, CalendarDays, ChevronRight, Compass, Globe2, LogOut, Map, Menu, Route, Share2, Sparkles, X } from 'lucide-react';
import { ApiError, apiRequest } from './lib/api';
import { calculateStats } from './lib/stats';
import { ExploreView } from './components/ExploreView';
import { KoreaMap, statusMeta } from './components/KoreaMap';
import { Landing } from './components/Landing';
import { PlansView } from './components/PlansView';
import { PublicProfile } from './components/PublicProfile';
import { RegionPanel } from './components/RegionPanel';
import { ShareView } from './components/ShareView';
import { StatsView } from './components/StatsView';
import type { BootstrapData, Meta, Place, PlaceProgress, RegionProgress, TravelPlan, User } from './types';

type Tab = 'map' | 'explore' | 'plans' | 'stats' | 'share';
type DraftPlan = Omit<TravelPlan, 'id'> & { id?: string };
const tabMeta: Array<{ id: Tab; label: string; icon: typeof Map }> = [
  { id: 'map', label: '여행지도', icon: Map },
  { id: 'explore', label: '컬렉션', icon: Route },
  { id: 'plans', label: '여행 계획', icon: CalendarDays },
  { id: 'stats', label: '통계·배지', icon: BarChart3 },
  { id: 'share', label: '공유', icon: Share2 },
];

export default function App() {
  const publicMatch = window.location.pathname.match(/^\/u\/([a-z0-9-]+)\/?$/u);
  if (publicMatch?.[1]) return <PublicProfile slug={publicMatch[1]} />;
  return <PrivateApp />;
}

function PrivateApp() {
  const [meta, setMeta] = useState<Meta | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [csrf, setCsrf] = useState('');
  const [data, setData] = useState<BootstrapData | null>(null);
  const [loading, setLoading] = useState(true);
  const [authBusy, setAuthBusy] = useState(false);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const [tab, setTab] = useState<Tab>('map');
  const [selectedRegionId, setSelectedRegionId] = useState('seoul');
  const [mobileNav, setMobileNav] = useState(false);

  const showNotice = useCallback((tone: 'success' | 'error', text: string) => {
    setNotice({ tone, text });
    window.setTimeout(() => setNotice(null), 3200);
  }, []);

  const loadBootstrap = useCallback(async () => {
    const bootstrap = await apiRequest<BootstrapData>('/api/bootstrap');
    setData(bootstrap);
    setUser(bootstrap.user);
    if (!bootstrap.regions.some((region) => region.id === selectedRegionId)) setSelectedRegionId(bootstrap.regions[0]?.id ?? 'seoul');
  }, [selectedRegionId]);

  useEffect(() => {
    const initialize = async () => {
      try {
        const nextMeta = await apiRequest<Meta>('/api/meta');
        setMeta(nextMeta);
        const session = await apiRequest<{ user: User; csrfToken: string }>('/api/auth/me');
        setUser(session.user); setCsrf(session.csrfToken);
        const bootstrap = await apiRequest<BootstrapData>('/api/bootstrap');
        setData(bootstrap);
      } catch (error) {
        if (!(error instanceof ApiError) || error.status !== 401) showNotice('error', error instanceof Error ? error.message : '앱을 준비하지 못했습니다.');
      } finally { setLoading(false); }
    };
    void initialize();
  }, [showNotice]);

  const run = async (key: string, task: () => Promise<void>, success?: string) => {
    setBusyKey(key);
    try { await task(); if (success) showNotice('success', success); }
    catch (error) { showNotice('error', error instanceof Error ? error.message : '요청을 처리하지 못했습니다.'); }
    finally { setBusyKey(null); }
  };

  const devLogin = async () => {
    setAuthBusy(true);
    try {
      const session = await apiRequest<{ user: User; csrfToken: string }>('/api/auth/dev', { method: 'POST' });
      setUser(session.user); setCsrf(session.csrfToken);
      const bootstrap = await apiRequest<BootstrapData>('/api/bootstrap'); setData(bootstrap);
    } catch (error) { showNotice('error', error instanceof Error ? error.message : '데모 계정을 열지 못했습니다.'); }
    finally { setAuthBusy(false); }
  };

  const saveRegion = (progress: RegionProgress) => run(`region:${progress.regionId}`, async () => {
    const { regionId, updatedAt, ...payload } = progress;
    const saved = await apiRequest<RegionProgress>(`/api/regions/${regionId}`, { method: 'PUT', body: JSON.stringify(payload) }, csrf);
    setData((current) => current ? { ...current, regionProgress: [...current.regionProgress.filter((item) => item.regionId !== saved.regionId), saved] } : current);
  }, '지역 기록을 저장했습니다.');

  const savePlace = (placeId: string, progress: PlaceProgress) => run(`place:${placeId}`, async () => {
    const { placeId: bodyPlaceId, updatedAt, ...payload } = progress;
    const saved = await apiRequest<PlaceProgress>(`/api/places/${placeId}/progress`, { method: 'PUT', body: JSON.stringify(payload) }, csrf);
    setData((current) => current ? { ...current, placeProgress: [...current.placeProgress.filter((item) => item.placeId !== placeId), saved] } : current);
  }, '장소 기록을 저장했습니다.');

  const addPlace = (input: { regionId: string; kind: 'attraction' | 'food'; name: string; subtitle: string; themes: string[] }) => run('add-place', async () => {
    const place = await apiRequest<Place>('/api/places', { method: 'POST', body: JSON.stringify(input) }, csrf);
    setData((current) => current ? { ...current, places: [...current.places, place] } : current);
  }, '나만의 장소를 추가했습니다.');

  const deletePlace = (placeId: string) => run(`place:${placeId}`, async () => {
    await apiRequest<{ ok: boolean }>(`/api/places/${placeId}`, { method: 'DELETE' }, csrf);
    setData((current) => current ? { ...current, places: current.places.filter((place) => place.id !== placeId), placeProgress: current.placeProgress.filter((item) => item.placeId !== placeId) } : current);
  }, '장소를 삭제했습니다.');

  const saveProfile = (input: { displayName: string; publicSlug: string; bio: string; isProfilePublic: boolean }) => run('profile', async () => {
    const saved = await apiRequest<User>('/api/profile', { method: 'PUT', body: JSON.stringify(input) }, csrf);
    setUser(saved); setData((current) => current ? { ...current, user: saved } : current);
  }, '공개 프로필 설정을 저장했습니다.');

  const savePlan = (draft: DraftPlan) => run('plan', async () => {
    const { id, createdAt, updatedAt, ...rest } = draft;
    const payload = { ...rest, items: rest.items.map(({ id: itemId, ...item }) => item) };
    await apiRequest<TravelPlan>(id ? `/api/plans/${id}` : '/api/plans', { method: id ? 'PUT' : 'POST', body: JSON.stringify(payload) }, csrf);
    await loadBootstrap();
  }, draft.id ? '여행 계획을 수정했습니다.' : '여행 계획을 만들었습니다.');

  const deletePlan = (planId: string) => run('plan', async () => {
    await apiRequest<{ ok: boolean }>(`/api/plans/${planId}`, { method: 'DELETE' }, csrf);
    setData((current) => current ? { ...current, plans: current.plans.filter((plan) => plan.id !== planId) } : current);
  }, '여행 계획을 삭제했습니다.');

  const logout = () => run('logout', async () => {
    await apiRequest<{ ok: boolean }>('/api/auth/logout', { method: 'POST' }, csrf);
    setUser(null); setData(null); setCsrf(''); setTab('map');
  });

  const openRegion = (regionId: string) => { setSelectedRegionId(regionId); setTab('map'); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  if (loading) return <main className="page-loading"><span /><p>여행지도를 준비하는 중…</p></main>;
  if (!user || !data) return <><Landing meta={meta} busy={authBusy} onDevLogin={devLogin} />{notice && <Toast notice={notice} />}</>;

  const selectedRegion = data.regions.find((region) => region.id === selectedRegionId) ?? data.regions[0];
  const stats = calculateStats(data);
  const selectedProgress = data.regionProgress.find((progress) => progress.regionId === selectedRegion?.id);
  const selectedPlaces = data.places.filter((place) => place.regionId === selectedRegion?.id);
  const selectedComplete = selectedPlaces.filter((place) => data.placeProgress.find((progress) => progress.placeId === place.id)?.isChecked).length;

  return <div className="app-shell">
    <header className="app-header"><a className="brand" href="/"><span className="brand-mark">ㅎ</span><span>한반도 컬렉션</span></a><nav className={mobileNav ? 'is-open' : ''}>{tabMeta.map((item) => { const Icon = item.icon; return <button type="button" key={item.id} className={tab === item.id ? 'is-active' : ''} onClick={() => { setTab(item.id); setMobileNav(false); }}><Icon size={16} />{item.label}</button>; })}</nav><div className="profile-menu"><span className="avatar">{user.avatarUrl ? <img src={user.avatarUrl} alt="" referrerPolicy="no-referrer" /> : user.displayName.slice(0, 1)}</span><div><b>{user.displayName}</b><small>{stats.level}</small></div><button type="button" className="icon-button" aria-label="로그아웃" onClick={logout}><LogOut size={17} /></button></div><button type="button" className="mobile-menu" aria-label="메뉴" onClick={() => setMobileNav((value) => !value)}>{mobileNav ? <X /> : <Menu />}</button></header>
    <main className="app-main">
      {tab === 'map' && <>
        <section className="dashboard-hero"><div><span className="eyebrow"><Sparkles size={14} /> {new Date().getMonth() + 1}월의 여행 컬렉션</span><h1>{user.displayName}님, 지도가<br /><em>{stats.percentage}%</em> 채워졌어요.</h1><p>오늘도 한 곳의 기억을 더해 나만의 대한민국을 완성해 보세요.</p></div><div className="hero-metrics"><article><span>전국 진행률</span><b>{stats.percentage}<small>%</small></b><i><em style={{ width: `${stats.percentage}%` }} /></i></article><article><span>방문 지역</span><b>{stats.completed}<small>/17</small></b><p>{stats.level}</p></article><article><span>수집 장소</span><b>{stats.checkedPlaces}</b><p>다음 후보 {stats.nextCandidates}곳</p></article></div></section>
        <section className="map-dashboard">
          <aside className="map-card"><header><div><span className="eyebrow"><Globe2 size={13} /> LIVE MAP</span><h2>나의 대한민국</h2></div><button type="button" className="text-button" onClick={() => setTab('explore')}>전체 지역 <ChevronRight size={15} /></button></header><KoreaMap regions={data.regions} progress={data.regionProgress} selectedId={selectedRegionId} onSelect={setSelectedRegionId} /><div className="selected-region-summary"><span style={{ background: statusMeta[selectedProgress?.status ?? 'unvisited'].color }}>{selectedRegion?.shortName}</span><div><b>{selectedRegion?.name}</b><small>{statusMeta[selectedProgress?.status ?? 'unvisited'].label} · 장소 {selectedComplete}/{selectedPlaces.length}</small></div><button type="button" aria-label="지역 상세" onClick={() => document.querySelector('.region-panel')?.scrollIntoView({ behavior: 'smooth' })}><ChevronRight /></button></div></aside>
          {selectedRegion && <RegionPanel region={selectedRegion} progress={selectedProgress} places={data.places} placeProgress={data.placeProgress} busyKey={busyKey} onSaveRegion={saveRegion} onSavePlace={savePlace} onAddPlace={addPlace} onDeletePlace={deletePlace} />}
        </section>
      </>}
      {tab === 'explore' && <ExploreView data={data} onOpenRegion={openRegion} />}
      {tab === 'plans' && <PlansView regions={data.regions} places={data.places} plans={data.plans} busy={busyKey === 'plan'} onSave={savePlan} onDelete={deletePlan} />}
      {tab === 'stats' && <StatsView data={data} />}
      {tab === 'share' && <ShareView user={user} busy={busyKey === 'profile'} onSave={saveProfile} />}
    </main>
    <nav className="mobile-bottom-nav">{tabMeta.slice(0, 4).map((item) => { const Icon = item.icon; return <button type="button" key={item.id} className={tab === item.id ? 'is-active' : ''} onClick={() => setTab(item.id)}><Icon size={19} /><span>{item.label.replace('·배지', '')}</span></button>; })}<button type="button" className={tab === 'share' ? 'is-active' : ''} onClick={() => setTab('share')}><Compass size={19} /><span>공유</span></button></nav>
    {notice && <Toast notice={notice} />}
  </div>;
}

function Toast({ notice }: { notice: { tone: 'success' | 'error'; text: string } }) {
  return <div className={`toast toast-${notice.tone}`} role="status">{notice.text}</div>;
}
